import * as R from 'ramda';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../../core/api/module';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { ActionType } from '../../../../core/redux/actionType';
import { DevItemComment } from '../models';


const KEY_ACTION = ActionType.UpdateDevItemCommentRequest;
const ACTIONS    = [
  ActionType.UpdateDevItemCommentRequest,
  ActionType.UpdateDevItemCommentResponse,
  ActionType.UpdateDevItemCommentError,
];


type Payload = string;


type Meta = {
  projectId: number;
  devItemId: number;
  commentId: number;
  comment: string;
};


/**
 * Dispatch this to update the content of a dev item comment.
 *
 * @param projectId - the database Id for the project containing the commented dev item
 * @param participantId - the database Id for the participant for whose dev item we're updating a comment
 * @param devItemId - the database Id for the commented dev item
 * @param commentId = the database Id for the edited comment
 * @param comment - the content of the updated comment
 */
export const updateDevItemCommentAction = (
  projectId: number,
  participantId: number,
  devItemId: number,
  commentId: number,
  comment: string,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.UpdateDevItemComment,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        commentId: commentId,
        comment:   comment,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId: projectId,
        devItemId: devItemId,
        commentId: commentId,
        comment:   comment,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${devItemId}-${commentId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId, devItemId, commentId, comment } = action.meta;

  const metaTag = `${projectId}-${devItemId}-${commentId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const itemComments = R.path<DevItemComment[]>(['devPlan', 'comments', `${projectId}`, `${devItemId}`], successState)
                       ?? [];

  const commentIndex = R.findIndex(
    comment => comment.commentId === commentId,
    itemComments,
  );

  const commentToUpdate = R.mergeRight(
    itemComments[commentIndex],
    {
      comment:     comment,
      lastUpdated: new Date(),
    } as DevItemComment,
  );

  return R.assocPath(
    ['devPlan', 'comments', `${projectId}`, `${devItemId}`, commentIndex],
    commentToUpdate,
    successState,
  );
};

export const updateDevItemCommentReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
